import type {ActionEnvelope, ChatState} from '@microsoft/agent-host-protocol'
import {isDirectTurnSurface} from '@github-ui/ahp-session-client/contracts'

import {recordTaskRepositoryScope, repositoryScopeFromRepositoryBacked} from './repository-scope'
import {turnMetrics as defaultTurnMetrics, type TurnMetrics} from './turn-metrics'
import {
  isValidDirectTurnTiming,
  type DirectTurnFinalizationReason,
  type DirectTurnSubmission,
  type EstablishedAhpDirectTurnTelemetry,
} from '@github-ui/ahp-session-client/contracts'

type TurnTerminalOutcome = 'completed' | 'cancelled' | 'error'
type TurnOutcome = TurnTerminalOutcome | 'unresolved'

const CLIENT_APPLICATION = 'copilot-cloud-web'

interface SubmissionAttempt extends Omit<DirectTurnSubmission, 'startPath' | 'context' | 'contextPending'> {
  startPath: DirectTurnSubmission['startPath']
  context?: DirectTurnSubmission['context']
  contextPending?: boolean
  readyAtSubmit: boolean
  submittedAtMs: number
  submittedInDocument: boolean
  readyAtMs?: number
  dispatchingAtMs?: number
}

interface ActiveTurnMeasurement extends SubmissionAttempt {
  turnId: string
  dispatchedAtMs: number
  acknowledgedAtMs?: number
  firstPartAtMs?: number
  firstContentAtMs?: number
  firstResponseEmitted: boolean
}

export interface TurnLatencyDependencies {
  now: () => number
  emit: (eventName: string, payload: Record<string, string | number | boolean | null | undefined>) => void
  /** Flush any batched analytics immediately, e.g. to deliver page-exit evidence before the document unloads. */
  flush?: () => void
  /** `performance.timeOrigin` of this document, to tell whether submit timing came from another document. */
  timeOrigin?: () => number
  page?: {
    subscribe(listener: (event: PageTransitionEvent) => void): () => void
  }
  turnMetrics?: Pick<
    TurnMetrics,
    | 'submitted'
    | 'discarded'
    | 'dispatched'
    | 'attributed'
    | 'preDispatchFailed'
    | 'acknowledged'
    | 'firstContent'
    | 'terminal'
    | 'disposed'
  >
}

const browserPage: TurnLatencyDependencies['page'] =
  typeof window === 'undefined'
    ? undefined
    : {
        subscribe: listener => {
          window.addEventListener('pagehide', listener)
          return () => window.removeEventListener('pagehide', listener)
        },
      }

function rememberRepositoryScope(context: DirectTurnSubmission['context'] | null | undefined): void {
  if (!context) return
  try {
    recordTaskRepositoryScope(context.taskId, repositoryScopeFromRepositoryBacked(context.repositoryBacked))
  } catch {
    // Best-effort analytics must not affect turn submission.
  }
}

function actionType(envelope: ActionEnvelope): string {
  return envelope.action.type
}

function actionTurnId(envelope: ActionEnvelope): string | undefined {
  const turnId = (envelope.action as {turnId?: unknown}).turnId
  return typeof turnId === 'string' ? turnId : undefined
}

function isUserTurnAcknowledgement(envelope: ActionEnvelope, turnId: string): boolean {
  if (actionType(envelope) !== 'chat/turnStarted' || actionTurnId(envelope) !== turnId) return false
  const origin = (envelope.action as {message?: {origin?: {kind?: unknown}}}).message?.origin?.kind
  return origin === 'user'
}

function isIgnoredAction(envelope: ActionEnvelope): boolean {
  const type = actionType(envelope)
  return type.startsWith('session/') || type === 'chat/activity' || type === 'chat/status'
}

function responsePartsCount(state: ChatState | undefined, turnId: string): number {
  return state?.activeTurn?.id === turnId && Array.isArray(state.activeTurn.responseParts)
    ? state.activeTurn.responseParts.length
    : 0
}

function markdownContentForAction(
  envelope: ActionEnvelope,
  state: ChatState | undefined,
  turnId: string,
): string | undefined {
  if (state?.activeTurn?.id !== turnId || !Array.isArray(state.activeTurn.responseParts)) return undefined
  const action = envelope.action as {type: string; part?: {id?: unknown}; partId?: unknown}
  const partId =
    action.type === 'chat/responsePart'
      ? action.part && typeof action.part.id === 'string'
        ? action.part.id
        : undefined
      : action.type === 'chat/delta' && typeof action.partId === 'string'
        ? action.partId
        : undefined
  if (!partId) return undefined
  const part = state.activeTurn.responseParts.find(candidate => 'id' in candidate && candidate.id === partId)
  return part?.kind === 'markdown' ? part.content : undefined
}

function outcomeFor(envelope: ActionEnvelope, turnId: string): TurnTerminalOutcome | undefined {
  if (actionTurnId(envelope) !== turnId) return undefined
  switch (actionType(envelope)) {
    case 'chat/turnComplete':
      return 'completed'
    case 'chat/turnCancelled':
      return 'cancelled'
    case 'chat/error':
      return 'error'
    default:
      return undefined
  }
}

function outcomeForState(state: ChatState | undefined, turnId: string): TurnTerminalOutcome | undefined {
  const turn = state?.turns.find(candidate => candidate.id === turnId)
  switch (turn?.state) {
    case 'complete':
      return 'completed'
    case 'cancelled':
      return 'cancelled'
    case 'error':
      return 'error'
    default:
      return undefined
  }
}

export function createBrowserTurnLatencyTracker(
  dependencies: TurnLatencyDependencies,
): EstablishedAhpDirectTurnTelemetry {
  const attempts = new Map<string, SubmissionAttempt>()
  const deferredEvents = new Map<
    string,
    Array<{eventName: string; payload: Record<string, string | number | boolean | null | undefined>}>
  >()
  const resolvedContexts = new Map<string, DirectTurnSubmission['context'] | null>()
  const pendingAttributionTurns = new Map<string, string>()
  let activeTurn: ActiveTurnMeasurement | undefined
  let currentReadyAtMs: number | undefined
  let currentReadinessLeadConsumed = false
  let unsubscribePage: (() => void) | undefined
  const turns = dependencies.turnMetrics ?? defaultTurnMetrics
  const turnMetricsOwner = {}
  const metrics = (callback: () => void) => {
    try {
      callback()
    } catch {
      // Best-effort analytics must not affect turn submission or action folding.
    }
  }
  const turnSurface = (measurement: SubmissionAttempt) =>
    measurement.context?.surface ?? (isDirectTurnSurface(measurement.surface) ? measurement.surface : undefined)
  const turnSubmission = (measurement: SubmissionAttempt) => ({
    requestId: measurement.requestId,
    taskId: measurement.context?.taskId,
    surface: turnSurface(measurement),
    turnKind: measurement.turnKind,
    startPath: measurement.startPath,
    readyAtSubmit: measurement.readyAtSubmit,
    modelId: measurement.modelId,
    submittedAtMs: measurement.submittedAtMs,
    submittedInDocument: measurement.submittedInDocument,
  })
  const emit = (eventName: string, payload: Record<string, string | number | boolean | null | undefined>): void => {
    try {
      dependencies.emit(eventName, {...payload, client_application: CLIENT_APPLICATION})
    } catch {
      // Best-effort analytics must not affect turn submission or action folding.
    }
  }

  const contextFields = (measurement: ActiveTurnMeasurement) =>
    measurement.context
      ? {
          surface: measurement.context.surface,
          environment_id: measurement.context.environmentId,
          task_id: measurement.context.taskId,
          session_id: measurement.context.sessionId,
          repository_backed: measurement.context.repositoryBacked,
        }
      : isDirectTurnSurface(measurement.surface)
        ? {surface: measurement.surface}
        : {}

  const resolvedContextFields = (context: DirectTurnSubmission['context']) =>
    context
      ? {
          surface: context.surface,
          environment_id: context.environmentId,
          task_id: context.taskId,
          session_id: context.sessionId,
          repository_backed: context.repositoryBacked,
        }
      : {}

  const emitMeasurement = (
    eventName: string,
    measurement: ActiveTurnMeasurement,
    payload: Record<string, string | number | boolean | null | undefined>,
  ) => {
    if (measurement.contextPending) {
      const events = deferredEvents.get(measurement.requestId) ?? []
      events.push({eventName, payload})
      deferredEvents.set(measurement.requestId, events)
      return
    }
    emit(eventName, payload)
  }

  const commonFields = (measurement: ActiveTurnMeasurement) => ({
    chat_version: 'v2',
    turn_kind: measurement.turnKind,
    start_path: measurement.startPath,
    ready_at_submit: measurement.readyAtSubmit,
    turn_id: measurement.turnId,
    ...(measurement.modelId ? {model: measurement.modelId} : {}),
    ...contextFields(measurement),
  })

  const emitLatency = (
    eventName: string,
    measurement: ActiveTurnMeasurement,
    elapsedMs: number,
    fields: Record<string, string> = {},
  ) => {
    emitMeasurement(eventName, measurement, {
      ...commonFields(measurement),
      elapsed_ms: elapsedMs,
      ...fields,
    })
  }
  const flushDeferredEvents = (requestId: string, context: DirectTurnSubmission['context'] | null | undefined) => {
    const events = deferredEvents.get(requestId)
    if (!events) return
    deferredEvents.delete(requestId)
    const fields = resolvedContextFields(context ?? undefined)
    for (const event of events) emit(event.eventName, {...event.payload, ...fields})
  }
  const finalizeActiveTurn = (
    outcome: TurnOutcome,
    unresolvedReason?: DirectTurnFinalizationReason,
    observedAtMs = dependencies.now(),
  ) => {
    const measurement = activeTurn
    if (!measurement) return
    if (outcome !== 'unresolved') metrics(() => turns.terminal(measurement.turnId, outcome, observedAtMs))
    const terminalFields = {
      outcome,
      ...(unresolvedReason ? {unresolved_reason: unresolvedReason} : {}),
    }
    if (outcome === 'unresolved') {
      // Lifecycle boundary: attribution may never resolve, so release any buffered
      // evidence and deliver the disposition directly instead of re-buffering it.
      flushDeferredEvents(measurement.requestId, measurement.context)
      emit('copilot_chat.submit_to_terminal_latency', {
        ...commonFields(measurement),
        elapsed_ms: observedAtMs - measurement.submittedAtMs,
        ...terminalFields,
      })
    } else {
      emitLatency(
        'copilot_chat.submit_to_terminal_latency',
        measurement,
        observedAtMs - measurement.submittedAtMs,
        terminalFields,
      )
    }
    activeTurn = undefined
  }
  const ensurePageObservation = () => {
    const page = dependencies.page ?? browserPage
    unsubscribePage ??= page?.subscribe(event => {
      if (event.persisted) return
      finalizeActiveTurn('unresolved', 'page_exit')
      // Deliver exit evidence immediately; a departing document may never flush Hydro's batch before unloading.
      dependencies.flush?.()
    })
  }

  return {
    readinessChanged: ready => {
      if (!ready) {
        currentReadyAtMs = undefined
        currentReadinessLeadConsumed = false
        for (const attempt of attempts.values()) {
          attempt.readyAtMs = undefined
        }
        return
      }
      if (currentReadyAtMs !== undefined) return
      currentReadyAtMs = dependencies.now()
      currentReadinessLeadConsumed = false
      for (const attempt of attempts.values()) {
        if (attempt.readyAtMs === undefined) attempt.readyAtMs = currentReadyAtMs
      }
    },
    beginSubmission: submission => {
      const existing = attempts.get(submission.requestId)
      if (existing) {
        rememberRepositoryScope(submission.context)
        const updated: SubmissionAttempt = {
          ...existing,
          ...(submission.modelId ? {modelId: submission.modelId} : {}),
          ...(isDirectTurnSurface(submission.surface) ? {surface: submission.surface} : {}),
          ...(submission.contextPending !== undefined ? {contextPending: submission.contextPending} : {}),
          ...(submission.context ? {context: {...submission.context}, contextPending: false} : {}),
        }
        attempts.set(submission.requestId, updated)
        metrics(() => turns.submitted(turnSubmission(updated), turnMetricsOwner))
        ensurePageObservation()
        return
      }
      const timing = isValidDirectTurnTiming(submission.timing) ? submission.timing : undefined
      const submittedAtMs = timing ? timing.timeOriginMs + timing.nowMs : dependencies.now()
      const submittedInDocument =
        !timing || !dependencies.timeOrigin || Math.abs(timing.timeOriginMs - dependencies.timeOrigin()) < 1
      const readyAtSubmit = currentReadyAtMs !== undefined && currentReadyAtMs <= submittedAtMs
      const resolvedContext = resolvedContexts.get(submission.requestId)
      resolvedContexts.delete(submission.requestId)
      rememberRepositoryScope(submission.context ?? resolvedContext)
      const attempt: SubmissionAttempt = {
        ...submission,
        ...(resolvedContext ? {context: {...resolvedContext}} : {}),
        ...(resolvedContext !== undefined ? {contextPending: false} : {}),
        ...(submission.context ? {context: {...submission.context}} : {}),
        readyAtSubmit,
        submittedAtMs,
        submittedInDocument,
        ...(currentReadyAtMs !== undefined
          ? {readyAtMs: currentReadinessLeadConsumed ? submittedAtMs : currentReadyAtMs}
          : {}),
      }
      attempts.set(submission.requestId, attempt)
      metrics(() => turns.submitted(turnSubmission(attempt), turnMetricsOwner))
      ensurePageObservation()
    },
    updateSubmissionAttribution: (requestId, context, modelId) => {
      rememberRepositoryScope(context)
      const attempt = attempts.get(requestId)
      if (!attempt) return
      const updated = {
        ...attempt,
        ...(modelId ? {modelId} : {}),
        context: {...context},
      }
      attempts.set(requestId, updated)
      metrics(() => turns.submitted(turnSubmission(updated), turnMetricsOwner))
    },
    discardSubmission: requestId => {
      if (attempts.delete(requestId)) metrics(() => turns.discarded(requestId))
    },
    readinessRequired: requestId => {
      const attempt = attempts.get(requestId)
      if (!attempt) return
      currentReadyAtMs = undefined
      currentReadinessLeadConsumed = false
      attempt.readyAtSubmit = false
      attempt.readyAtMs = undefined
      if (attempt.startPath === 'active') attempt.startPath = 'resume'
    },
    readinessObserved: requestId => {
      const attempt = attempts.get(requestId)
      if (!attempt) return
      const observedAtMs = dependencies.now()
      if (currentReadyAtMs === undefined) {
        currentReadyAtMs = observedAtMs
        currentReadinessLeadConsumed = false
      }
      attempt.readyAtMs ??= currentReadinessLeadConsumed ? attempt.submittedAtMs : currentReadyAtMs
    },
    turnDispatching: requestId => {
      const attempt = attempts.get(requestId)
      if (attempt && attempt.dispatchingAtMs === undefined) attempt.dispatchingAtMs = dependencies.now()
    },
    turnDispatched: (requestId, turnId) => {
      const attempt = attempts.get(requestId)
      if (!attempt || attempt.dispatchingAtMs === undefined) return
      activeTurn = {
        ...attempt,
        turnId,
        dispatchedAtMs: attempt.dispatchingAtMs,
        firstResponseEmitted: false,
      }
      attempts.delete(requestId)
      const dispatched = activeTurn
      metrics(() =>
        turns.dispatched(
          {
            ...turnSubmission(dispatched),
            turnId,
            dispatchedAtMs: dispatched.dispatchedAtMs,
            attributionPending: dispatched.contextPending,
          },
          turnMetricsOwner,
        ),
      )
      if (dispatched.contextPending) pendingAttributionTurns.set(requestId, turnId)
      if (activeTurn.readyAtMs !== undefined) {
        emitLatency(
          'copilot_chat.readiness_relative_to_submit_latency',
          activeTurn,
          activeTurn.readyAtMs - activeTurn.submittedAtMs,
        )
        if (!currentReadinessLeadConsumed && activeTurn.readyAtMs === currentReadyAtMs) {
          currentReadinessLeadConsumed = true
          for (const pendingAttempt of attempts.values()) {
            if (pendingAttempt.readyAtMs === currentReadyAtMs) pendingAttempt.readyAtMs = pendingAttempt.submittedAtMs
          }
        }
      }
      emitLatency(
        'copilot_chat.submit_to_dispatch_latency',
        activeTurn,
        activeTurn.dispatchedAtMs - activeTurn.submittedAtMs,
      )
    },
    submissionContextResolved: (requestId, context) => {
      rememberRepositoryScope(context)
      const attempt = attempts.get(requestId)
      if (attempt) {
        attempt.contextPending = false
        if (context) attempt.context = {...context}
        metrics(() => turns.submitted(turnSubmission(attempt), turnMetricsOwner))
      }
      if (activeTurn?.requestId === requestId) {
        activeTurn.contextPending = false
        if (context) activeTurn.context = {...context}
      }
      const pendingTurnId = pendingAttributionTurns.get(requestId)
      if (pendingTurnId) {
        pendingAttributionTurns.delete(requestId)
        metrics(() => turns.attributed(pendingTurnId, context?.taskId, context?.surface))
      }
      const events = deferredEvents.get(requestId)
      if (!attempt && activeTurn?.requestId !== requestId && !pendingTurnId && !events) {
        resolvedContexts.set(requestId, context ? {...context} : null)
        return
      }
      flushDeferredEvents(requestId, context)
    },
    preDispatchError: requestId => {
      const attempt = attempts.get(requestId)
      if (attempt) metrics(() => turns.preDispatchFailed(turnSubmission(attempt), turnMetricsOwner))
      attempts.delete(requestId)
      resolvedContexts.delete(requestId)
    },
    acceptedAction: (envelope, previousState, state) => {
      const measurement = activeTurn
      if (!measurement) return
      const observedAtMs = dependencies.now()
      const acknowledged = isUserTurnAcknowledgement(envelope, measurement.turnId)
      if (acknowledged && measurement.acknowledgedAtMs === undefined) {
        measurement.acknowledgedAtMs = observedAtMs
        metrics(() => turns.acknowledged(measurement.turnId, observedAtMs))
        emitLatency(
          'copilot_chat.dispatch_to_acknowledgement_latency',
          measurement,
          observedAtMs - measurement.dispatchedAtMs,
        )
      }

      const exactTurnAction = actionTurnId(envelope) === measurement.turnId
      const acknowledgedInputRequest =
        actionType(envelope) === 'chat/inputRequested' &&
        measurement.acknowledgedAtMs !== undefined &&
        state?.activeTurn?.id === measurement.turnId
      const belongsToTurn = !isIgnoredAction(envelope) && (exactTurnAction || acknowledgedInputRequest)
      if (!belongsToTurn) return

      if (!acknowledged && !measurement.firstResponseEmitted) {
        measurement.firstResponseEmitted = true
        emitMeasurement('copilot_chat.first_response_latency', measurement, {
          chat_version: 'v2',
          turn_kind: measurement.turnKind,
          turn_id: measurement.turnId,
          elapsed_ms: observedAtMs - measurement.dispatchedAtMs,
          ...(measurement.modelId ? {model: measurement.modelId} : {}),
          ...contextFields(measurement),
        })
      }

      if (
        measurement.firstPartAtMs === undefined &&
        responsePartsCount(state, measurement.turnId) > responsePartsCount(previousState, measurement.turnId)
      ) {
        measurement.firstPartAtMs = observedAtMs
        emitLatency('copilot_chat.submit_to_first_part_latency', measurement, observedAtMs - measurement.submittedAtMs)
      }

      if (
        measurement.firstContentAtMs === undefined &&
        (actionType(envelope) === 'chat/responsePart' || actionType(envelope) === 'chat/delta') &&
        markdownContentForAction(envelope, state, measurement.turnId)?.trim()
      ) {
        measurement.firstContentAtMs = observedAtMs
        metrics(() => turns.firstContent(measurement.turnId, observedAtMs))
        emitLatency(
          'copilot_chat.submit_to_first_content_latency',
          measurement,
          observedAtMs - measurement.submittedAtMs,
        )
      }

      const outcome = outcomeFor(envelope, measurement.turnId)
      if (outcome) finalizeActiveTurn(outcome, undefined, observedAtMs)
    },
    observedState: state => {
      const measurement = activeTurn
      if (!measurement) return
      const outcome = outcomeForState(state, measurement.turnId)
      if (!outcome) return
      // The relay notifies view subscribers before action listeners, so a live terminal action reaches this
      // snapshot reconciliation before `acceptedAction` can record first-response evidence. Defer finalization to a
      // microtask (preserving the observation time) so a live action wins; pure reconciliation, which arrives
      // without an accompanying action, still finalizes once the microtask runs.
      const observedAtMs = dependencies.now()
      queueMicrotask(() => {
        if (activeTurn !== measurement) return
        finalizeActiveTurn(outcome, undefined, observedAtMs)
      })
    },
    reset: (reason = 'disposed') => {
      finalizeActiveTurn('unresolved', reason)
      metrics(() => turns.disposed(turnMetricsOwner))
      attempts.clear()
      deferredEvents.clear()
      resolvedContexts.clear()
      pendingAttributionTurns.clear()
      activeTurn = undefined
      currentReadyAtMs = undefined
      currentReadinessLeadConsumed = false
      // Always drop the page listener: reset clears every measurement and the next beginSubmission reinstalls
      // observation, so retaining it only leaks the closure across task navigation (reset passes 'replaced').
      unsubscribePage?.()
      unsubscribePage = undefined
    },
  }
}
